import { Typography, Form, FormInstance, Button, Alert, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useProposerRecommendationPage } from '../ProposerRecommendationPage/useProposerRecommendationPage';
import { RecommendationComponent } from '../RecommendationComponent';
import {
	PredefinedReasonCategory,
	ReviewAction,
} from '../../../shared/Constants';
import { UserRole } from '../../../shared/VolunteerApplicationServiceTypes';
import useUserSession from '../../../hooks/useUserSession';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface SkipAllInterviewsCollectionPageProps {
	form: FormInstance;
}

const SectionHeader = ({
	heading,
	description,
}: {
	heading: string;
	description: string;
}) => {
	return (
		<div className="flex flex-col mb-[8px]">
			<Text className="font-sans font-bold text-lg text-primary-blue mt-6 mb-2.5">
				{heading}
			</Text>
			<Text className="mb-[8px]" type="secondary">
				{description}
			</Text>
		</div>
	);
};

export const SkipAllInterviewsCollectionPage = ({
	form,
}: SkipAllInterviewsCollectionPageProps) => {
	const { t } = useTranslation();
	const { selectedRole } = useUserSession();

	const isAdmin = selectedRole === UserRole.SNP_ADMIN;
	const showForwardToCareTeam = isAdmin;

	return (
		<div className="flex flex-col h-full min-h-0">
			<Form
				name="basic"
				layout="vertical"
				size="middle"
				labelCol={{ span: 16 }}
				initialValues={{
					remember: true,
					review: ReviewAction.REJECT,
				}}
				autoComplete="off"
				form={form}
			>
				<SectionHeader
					heading={t('proposerNotAbleToProceedWithInterview')}
					description={t('provideReasonWhyInterviewCannotBeDone')}
				/>
				<div className="flex flex-row items-start">
					<div className="flex flex-col w-full">
						<Text>{t('iRecommendTheCandidatesApplicationToBe')}</Text>
						<RecommendationComponent
							commentsMandatory
							commentsLabel="yourCommentsToSupportAboveRecommendation"
							commentsPlaceholder="commentsAreMandatory"
							form={form}
							category={PredefinedReasonCategory.SKIP}
							displayComments
							showForwardToCareTeam={showForwardToCareTeam}
						/>
					</div>
				</div>
			</Form>
		</div>
	);
};

export const SkipAllInterviewsCollectionPageContainer = () => {
	const { t } = useTranslation();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const {
		form,
		onSubmitClickForSkipAllInterviews,
		onCancelClick,
		submitButtonDisabled,
		application,
	} = useProposerRecommendationPage({ isSkipInterview: true });

	return (
		<div
			className={`flex flex-col h-full min-h-0 bg-white mx-auto rounded-lg mt-4 border border-solid border-gray-200 ${
				isMobile ? 'w-[95%] max-w-[90%] p-4' : 'w-3/4 max-w-[50%] p-8'
			}`}
		>
			<SkipAllInterviewsCollectionPage form={form} />
			<div className="flex flex-row p-[8px] justify-center">
				<Button onClick={onCancelClick}>{t('cancel')}</Button>
				<Button
					onClick={onSubmitClickForSkipAllInterviews}
					type="primary"
					disabled={submitButtonDisabled || application?.isApprover}
					className="ml-[16px]"
				>
					{t('submit')}
				</Button>
			</div>
			{application?.isApprover && (
				<Alert
					showIcon
					message={t('onlyProposersAreAllowedToSubmitFeedback')}
					type="warning"
				/>
			)}
		</div>
	);
};
